/**
 * MovieCard Component
 * Film recommendation card for the Movie Explore page
 */

import type { RecommendedMovie } from '../types/movie-explore.types';

interface MovieCardProps {
  movie: RecommendedMovie;
  onAddDirector?: (director: string) => void;
}

export default function MovieCard({ movie, onAddDirector }: MovieCardProps) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-all overflow-hidden">
      {/* Cinematic header area */}
      <div className="relative aspect-video bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex flex-col justify-end p-4">
        {/* Gradient overlay for text */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />

        {/* Year + Country badge */}
        <div className="relative z-10 flex items-center gap-2 mb-2">
          <span className="text-xs text-gray-300 font-medium">
            {movie.year} · {movie.country}
            {movie.runtime ? ` · ${movie.runtime}m` : ''}
          </span>
        </div>

        {/* Title */}
        <h3 className="relative z-10 text-xl font-bold text-white leading-tight mb-1">
          {movie.title}
        </h3>

        {/* Director */}
        <p className="relative z-10 text-sm text-gray-300">
          dir. {movie.director}
        </p>

        {/* Language badge (if foreign) */}
        {movie.language && movie.language.toLowerCase() !== 'english' && (
          <div className="absolute top-3 right-3 z-10">
            <span className="px-2 py-1 bg-black/60 text-white text-xs rounded-full">
              {movie.language}
            </span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        {/* Genre Tags */}
        {movie.genre_tags && movie.genre_tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {movie.genre_tags.map((tag) => (
              <span
                key={tag}
                className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full"
              >
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* Synopsis */}
        {movie.synopsis && (
          <p className="text-xs text-gray-500 mb-3 leading-relaxed italic">
            {movie.synopsis}
          </p>
        )}

        {/* Reasoning */}
        {movie.reasoning && (
          <p className="text-xs text-gray-600 mb-3 leading-relaxed">
            {movie.reasoning}
          </p>
        )}

        {/* Actions */}
        <div className="flex items-center gap-2 flex-wrap">
          {/* Letterboxd Link */}
          {movie.letterboxd_url && (
            <a
              href={movie.letterboxd_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200 transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 15H9V8h2v9zm4 0h-2V8h2v9z"/>
              </svg>
              Letterboxd
            </a>
          )}

          {/* MUBI Link */}
          {movie.mubi_url && (
            <a
              href={movie.mubi_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-700 bg-blue-50 rounded hover:bg-blue-100 transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
                <circle cx="12" cy="12" r="10"/>
              </svg>
              MUBI
            </a>
          )}

          {/* Add Director Button */}
          {onAddDirector && (
            <button
              onClick={() => onAddDirector(movie.director)}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-purple-700 bg-purple-50 rounded hover:bg-purple-100 transition-colors"
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Add Director
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
